import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { EmailPreviewFrame } from "@/components/admin/EmailPreviewFrame";
import { AdminPageSkeleton } from "@/components/admin/AdminPageSkeleton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Monitor, Smartphone } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

type EmailAutomation = {
  subscriptionReminders: { enabled: boolean };
  inactiveUsers: { enabled: boolean; daysSinceLogin: number };
  happyNewMonth: { enabled: boolean };
};

const SAMPLE_TYPES = [
  { value: "subscription_reminder", label: "Subscription reminder" },
  { value: "inactive_user", label: "Inactive user" },
  { value: "happy_new_month", label: "Happy new month" },
] as const;

export function EmailAutomationPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<EmailAutomation | null>(null);
  const [userId, setUserId] = useState("");
  const [sampleType, setSampleType] =
    useState<(typeof SAMPLE_TYPES)[number]["value"]>("subscription_reminder");
  const [sending, setSending] = useState(false);
  const [showInactiveSendDialog, setShowInactiveSendDialog] = useState(false);
  const [sendingInactive, setSendingInactive] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/platform-analytics/email-automation");
        if (!cancelled) setSettings(data);
      } catch {
        toast.error("Failed to load email automation settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const { data } = await api.get(
          `/api/platform-analytics/email-automation/preview?type=${sampleType}`,
        );
        if (!cancelled) setPreviewHtml(data.html || "");
      } catch {
        if (!cancelled) setPreviewHtml("");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sampleType]);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      await api.patch("/api/platform-analytics/email-automation", settings);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendInactiveUsersNow = async () => {
    if (!settings) return;
    setSendingInactive(true);
    try {
      const { data } = await api.post(
        "/api/platform-analytics/email-automation/send-inactive-users",
        { daysSinceLogin: settings.inactiveUsers.daysSinceLogin },
      );
      if (data.skipped) {
        toast.message("Inactive user emails are disabled in settings");
        return;
      }
      const failedCount = data.failed?.length ?? 0;
      if (failedCount > 0) {
        toast.warning(
          `Sent ${data.sent} of ${data.candidates} emails (${failedCount} failed)`,
        );
      } else {
        toast.success(
          data.message ||
            `Sent ${data.sent} of ${data.candidates} inactive user email(s)`,
        );
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to send emails";
      toast.error(msg);
    } finally {
      setSendingInactive(false);
      setShowInactiveSendDialog(false);
    }
  };

  const sendSample = async () => {
    if (!userId.trim()) {
      toast.error("Enter a user ID");
      return;
    }
    setSending(true);
    try {
      await api.post("/api/platform-analytics/email-automation/send-sample", {
        type: sampleType,
        userId: userId.trim(),
      });
      toast.success("Sample email sent");
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to send";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (loading || !settings) {
    return <AdminPageSkeleton variant="form" />;
  }

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Email automation" />
      <PageHeader
        title="Email automation"
        subtitle="Cron emails use the same branded Sqaleshop layout as order and campaign mail."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
        <div className="space-y-5">
          <div className={dashboardStyles.sectionCard}>
            <h2 className={dashboardStyles.sectionTitle}>Toggles</h2>
            <div className="mt-5 space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-heading font-semibold">Subscription reminders</p>
                  <p className="text-xs text-muted-foreground">
                    Sent a few days before Premium ends.
                  </p>
                </div>
                <Switch
                  checked={settings.subscriptionReminders.enabled}
                  onCheckedChange={(v) =>
                    setSettings({
                      ...settings,
                      subscriptionReminders: { enabled: v },
                    })
                  }
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-heading font-semibold">Inactive users</p>
                  <p className="text-xs text-muted-foreground">
                    Owners who have not logged in for a while.
                  </p>
                </div>
                <Switch
                  checked={settings.inactiveUsers.enabled}
                  onCheckedChange={(v) =>
                    setSettings({
                      ...settings,
                      inactiveUsers: { ...settings.inactiveUsers, enabled: v },
                    })
                  }
                />
              </div>

              <div className="space-y-3 rounded-2xl bg-[#F5F5F3] p-4">
                <div className="space-y-2">
                  <Label>Days since login</Label>
                  <Input
                    type="number"
                    min={7}
                    max={365}
                    value={settings.inactiveUsers.daysSinceLogin}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        inactiveUsers: {
                          ...settings.inactiveUsers,
                          daysSinceLogin: Number(e.target.value) || 30,
                        },
                      })
                    }
                    className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInactiveSendDialog(true)}
                  disabled={sendingInactive}
                  className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
                >
                  {sendingInactive ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Send to inactive users now
                </Button>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-heading font-semibold">Happy new month</p>
                  <p className="text-xs text-muted-foreground">
                    On the 1st of each month for store owners.
                  </p>
                </div>
                <Switch
                  checked={settings.happyNewMonth.enabled}
                  onCheckedChange={(v) =>
                    setSettings({
                      ...settings,
                      happyNewMonth: { enabled: v },
                    })
                  }
                />
              </div>

              <Button
                onClick={save}
                disabled={saving}
                className="h-10 rounded-full px-5 text-sm font-semibold shadow-none"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save changes
              </Button>
            </div>
          </div>

          <div className={dashboardStyles.sectionCard}>
            <h2 className={dashboardStyles.sectionTitle}>Send a sample</h2>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label>Template</Label>
                <Select
                  value={sampleType}
                  onValueChange={(v) =>
                    setSampleType(v as (typeof SAMPLE_TYPES)[number]["value"])
                  }
                >
                  <SelectTrigger className={cn(dashboardStyles.filterSelect, "w-full")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SAMPLE_TYPES.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>User ID</Label>
                <Input
                  placeholder="Paste user _id"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
                />
              </div>
              <Button
                onClick={sendSample}
                disabled={sending}
                className="h-10 rounded-full px-5 text-sm font-semibold shadow-none"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send sample
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-heading font-semibold">Live preview</p>
            <div className="flex rounded-full border border-[#EBEBEB] bg-white p-1">
              <button
                type="button"
                onClick={() => setViewport("desktop")}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-semibold",
                  viewport === "desktop" ? "bg-[#EDE4FB] text-primary" : "text-muted-foreground",
                )}
              >
                <Monitor className="mr-1 inline h-3.5 w-3.5" />
                Desktop
              </button>
              <button
                type="button"
                onClick={() => setViewport("mobile")}
                className={cn(
                  "rounded-full px-3 py-1.5 text-xs font-semibold",
                  viewport === "mobile" ? "bg-[#EDE4FB] text-primary" : "text-muted-foreground",
                )}
              >
                <Smartphone className="mr-1 inline h-3.5 w-3.5" />
                Mobile
              </button>
            </div>
          </div>
          {previewLoading ? (
            <AdminPageSkeleton variant="form" />
          ) : previewHtml ? (
            <EmailPreviewFrame html={previewHtml} viewport={viewport} />
          ) : (
            <div className={dashboardStyles.sectionCard}>
              <p className="text-sm text-muted-foreground">Preview unavailable.</p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={showInactiveSendDialog}
        onOpenChange={setShowInactiveSendDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send inactive user emails?</AlertDialogTitle>
            <AlertDialogDescription>
              This will email store owners who have not logged in for at least{" "}
              {settings.inactiveUsers.daysSinceLogin} days, using the branded
              template. Save settings first if you changed the day threshold.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingInactive}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendInactiveUsersNow} disabled={sendingInactive}>
              {sendingInactive ? "Sending..." : "Send emails"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
